import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { LoadingScreen } from '@/components/shared/LoadingScreen';
import { PaywallDialog } from '@/components/shared/PaywallDialog';
import { TeamAddDialog } from '@/components/team/TeamAddDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamContext } from '@/contexts/TeamContext';
import { useSubscriptionFlow } from '@/hooks/useSubscriptionFlow';
import { useTeamInvites } from '@/hooks/useTeamInvites';
import { useTeams } from '@/hooks/useTeams';
import { PropsWithChildren, useState } from 'react';

// Handles the initialization of the app, checking if the user has any pending invites or teams before rendering the children which is the AuthenticatedApp component.
export const InitContainer = ({ children }: PropsWithChildren) => {
  const { activeTeam, setActiveTeam, isChangingTeam } = useTeamContext();
  const { signOut } = useAuth();
  const [isPaywallOpen, setIsPaywallOpen] = useState(true);
  const { handleNoSubscription } = useSubscriptionFlow();

  const {
    data: invites,
    isLoading: isLoadingInvites,
    acceptInvite,
  } = useTeamInvites();
  const { data: teams, isLoading: isLoadingTeams } = useTeams();

  const pendingInvite = invites?.[0] ?? null;

  const handlePaywallClose = async () => {
    setIsPaywallOpen(false);

    await handleNoSubscription();
  };

  const handleAcceptInvite = async () => {
    if (!pendingInvite) return;

    await acceptInvite.mutateAsync(pendingInvite.id);

    setActiveTeam(pendingInvite.team);
  };

  const handleDeclineInvite = async () => {
    if (!pendingInvite) return;

    await signOut();
  };

  if (isChangingTeam) {
    return <LoadingScreen message="Changing workspace..." />;
  }

  if (isLoadingInvites || isLoadingTeams) {
    return <LoadingScreen message="Checking your workspace..." />;
  }

  if (pendingInvite) {
    return (
      <>
        <LoadingScreen message="Team invitation pending..." />
        <ConfirmDialog
          open={true}
          onOpenChange={() => {}}
          title="Team Invitation"
          description={`You've been invited to join ${pendingInvite.team.name}'s workspace. Would you like to accept this invitation?`}
          confirmText="Accept"
          cancelText="Decline"
          onConfirm={handleAcceptInvite}
          onCancel={handleDeclineInvite}
          isConfirming={acceptInvite.isPending || isChangingTeam}
          variant="default"
        />
      </>
    );
  }

  if (!teams?.length) {
    return (
      <>
        <LoadingScreen message="Setting up your workspace..." />
        <TeamAddDialog open={true} onOpenChange={() => {}} />
      </>
    );
  }

  if (!!activeTeam && !activeTeam.subscription) {
    return <PaywallDialog open={true} onOpenChange={handlePaywallClose} />;
  }

  if (activeTeam) {
    return <>{children}</>;
  }

  return <LoadingScreen message="Initialising..." />;
};
